import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVCardContent,
  selectIsContentLoading,
  selectVCardContentSerieState,
  selectVCardPBIState,
} from './selectors/vcard.selector';
import Preview from './components/Preview';
import TabsSection from './components/TabsSection/TabsSection';
import TalentSection from './components/TalentsSection/TalentsSection';
import EpisodesSection from './components/EpisodesSection/EpisodesSection';
import Recommended from './components/Recommended/Recommended';
import './VCard.scss';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import {
  clearSeenlast,
  getInfoSerieByGroupId,
  getSeenLast,
} from './actions/content/content.actions';
import { pushGTMEvent } from 'containers/Player/utils/analytics';
import _ from 'lodash';
import getContentType from '../../utils/getContentType';
import getContentAvailability from '../../utils/getContentAvailability';
import deleteSymbols from '../../utils/deleteSymbols';
import { useNavigate } from 'react-router-dom';
import { useAddToFavoriteFromFreemium } from './components/hooks/useAddToFavoriteFromFreemium';
import { useFetchFavorites } from 'containers/NewSearchPage/hooks/useFetchFavorites';
import Metrics from 'services/metrics';

const EVENT_NAME = 'view_item';
const TYPE_MOVIE = 'pelicula';
const INVALID_CONTENT_TYPE = 'no aplica';
const INVALID_BRAND = 'no aplica';
const EPISODES_QUANTITY = '1';
const NODE_NAME = 'vcard';
const DEFAULT_PRICE = '0.00';

const parseGenres = genres => genres.flatMap(({ name }) => (name ? name : [])).join(', ');
const cleanGenres = genres => genres?.length > 0 ? genres : [];

const getPrice = infoPBI => {
  if (infoPBI?.playButton?.price) return infoPBI.playButton.price;

  const prices = infoPBI?.listButtons?.button?.map(button => button.price);
  return prices?.length > 0 ? prices.join(', ') : DEFAULT_PRICE;
};

const getEpisodeDetails = (contentType, media) => {
  if (contentType === TYPE_MOVIE) return INVALID_CONTENT_TYPE;
  return `temporada ${media?.season} episodio ${media?.number}`;
}

const VCardLoading = () => (
  <div className="vcardContainer">
    <section className="VCard">
      <Preview hasPreview={false} />
      <TabsSection isLoading />
    </section>
  </div>
);

const VCardContent = ({ groupId, _eventInfo = {} }) => {
  const info = useSelector(selectVCardContent);
  const { infoPBI } = useSelector(selectVCardPBIState);
  const [isPlaying, changePlay] = useState(false);
  const vcardRef = useRef(null);
  const eventSent = useRef(false);

  useEffect(() => {
    const contentDiv = document.querySelector('.content_div')
    if (contentDiv) contentDiv.scrollTo(0,0);
  }, []);

  useEffect(() => {
    if (!infoPBI?.playButton || eventSent.current) return;

    const { genres, episode, types, proveedorCode } = info || {};
    const contentType = getContentType(info);
    const categories = parseGenres(cleanGenres(genres));
    const episodeDetails = getEpisodeDetails(contentType, episode);
    const contentAvailability = getContentAvailability(types, proveedorCode).join(', ') || '';
    const price = getPrice(infoPBI);
    const proveedor = info?.proveedorCode || INVALID_BRAND;

    const eventInfo = {
      event: EVENT_NAME,
      item_id: info?.group_id,
      item_name: info?.title,
      item_category: contentType,
      item_category2: categories,
      item_category3: contentAvailability,
      item_category4: episodeDetails,
      price,
      quantity: EPISODES_QUANTITY,
      item_brand: proveedor,
      item_list_name: NODE_NAME,
    };

    Metrics.newEvent(eventInfo);
    eventSent.current = true;
  }, [infoPBI]);

  return (
    <div className="vcardContainer">
      <section className="VCard" ref={vcardRef}>
        <Preview
          hasPreview={info.hasPreview}
          backgroundImage={info.imageBaseHorizontal}
          groupId={groupId}
          isPlaying={isPlaying}
          changePlay={changePlay}
        />
        <TabsSection
          info={info}
          groupId={groupId}
          isPlaying={isPlaying}
          changePlay={changePlay}
        />
      </section>

      {info.isSerie && (
        <section style={{ display: info.isSerie ? 'block' : 'none' }}>
          <EpisodesSection
            groupId={groupId}
            episode={info.episode}
            title={info.title}
          />
        </section>
      )}

      <section>
        <Recommended groupId={groupId} />
      </section>

      <section>
        <TalentSection />
      </section>
    </div>
  );
};

const VCard = ({ isActive, isStatic, groupId, eventInfo = {} }) => {
  const isLoadingInfo = useSelector(selectIsContentLoading);
  const content = useSelector(selectVCardContent);
  const seenLast = useSelector(state => state.Vcard.seenLast);
  const serie = useSelector(selectVCardContentSerieState);
  const {user_id, userhash} = useSelector(state => state?.user) || {};
  const userHasSession= !!(user_id && userhash);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useFetchFavorites(groupId);
  useAddToFavoriteFromFreemium({ groupId });

  useEffect(() => {
    return () => {
      if (isActive && !isLoadingInfo && isStatic && content.group_id) {
        localStorage.removeItem('urlVcard');
        localStorage.removeItem('anonymousUserComeFromPlayer');
        localStorage.removeItem('addToFavorite');
      }
    };
  }, []);

  useEffect(() => {
    dispatch(clearSeenlast());
  }, []);

  useEffect(
    () => {
      if (content.group_id) {
        const contentType = getContentType(content);
        const contentAvailability = getContentAvailability(
          content.types,
          content.proveedorCode,
        );
        // Select content event
        if (
          !_.isEmpty(eventInfo) &&
          eventInfo.content_id === content.group_id
        ) {
          pushGTMEvent({
            ...eventInfo,
            content_type: contentType,
            content_category: deleteSymbols(
              content.genres
                .map(genre => genre.desc)
                .toString()
                .toLowerCase(),
            ),
            content_availability: contentAvailability.toString(),
          });
        }
      }
    },
    [content],
  );

  useEffect(
    () => {
      if (serie?.infoSerie.groupId === groupId) return;

      if (content?.isSerie) dispatch(getInfoSerieByGroupId(groupId));
    },
    [dispatch, content?.isSerie, groupId, serie?.infoSerie.groupId],
  );

  useEffect(
    () => {
      if (serie?.isLoadingInfoSerie || !serie?.infoSerie.id ) return;
      if (serie?.infoSerie.groupId !== groupId) return;
      if(!userHasSession) return

      if (!seenLast.isLoading && seenLast?.data === null) {
        dispatch(
          getSeenLast({
            group_id: serie?.infoSerie.groupId,
            serieId: serie?.infoSerie.id,
            navigate,
          }),
        );
      }
    },
    [serie?.infoSerie.id, serie?.isLoadingInfoSerie, seenLast?.data],
  );


  if (!isActive || isLoadingInfo || !isStatic || !content?.group_id)
    return <VCardLoading />;
  if (!content?.group_id && content?.isSerie && seenLast.isLoading)
    return <VCardLoading />;
  if (
    content.isSerie &&
    (!userHasSession ? groupId !== serie?.infoSerie.groupId : (seenLast.data === null || groupId !== serie?.infoSerie.groupId))
  )
    return <VCardLoading />;

  const result = (
    <>
      <VCardContent
        groupId={groupId}
        eventInfo={eventInfo}
      />
    </>
  );
  return isActive && result;
};

VCard.propTypes = {
  isActive: PropTypes.bool.isRequired,
  isStatic: PropTypes.bool,
};

VCard.defaultProps = {
  isStatic: false,
};

export default VCard;
